import React, { SetStateAction, useMemo, useState } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

import { RecipePresetSubstep, settingsInterface } from '../../../app/recipeCreation/createRecipe.types';
import { HeatingModeEnum } from '../utils/constants';
import { getTime, useCalculateTotalTime } from '../utils/getTime';
import PresetResetDialog from './PresetResetDialog';
import PresetsConfirmDialog from './PresetsConfirmDialog';
import styles from './presetSelectedView.module.css'

const PresetSelectedView = ({
  setIsPresetOpen,
  presetSelected,
  setPresetSelected,
  settings,
  setSettings,
}: {
  isPresetOpen: boolean;
  setIsPresetOpen: React.Dispatch<SetStateAction<boolean>>;
  presetSelected: RecipePresetSubstep | null;
  setPresetSelected: React.Dispatch<SetStateAction<RecipePresetSubstep | null>>;
  settings: Array<settingsInterface>;
  setSettings: React.Dispatch<SetStateAction<Array<settingsInterface>>>;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [confirmPopUpOpen, setConfirmPopUpOpen] = useState(false);
  const [resetPopUp, setResetPopUp] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  
  const confirmDialog = () => {
    setConfirmPopUpOpen(true);
  };

  const presetSettings = settings;
  const presetTime = useCalculateTotalTime(settings);
  
  const containsTATInstruction = useMemo(
    () =>
      !!presetSettings?.find((presetSettings: settingsInterface) =>
        [HeatingModeEnum.TAT, HeatingModeEnum.RICECOOKER].includes(
          presetSettings.heatingMode
        )
      ),
    [presetSettings]
  );
  
  return (
    <Grid className={styles.main_grid} container spacing={4}>
      <Grid className={styles.preset_view_grid} container direction="row">
        <Grid direction="column" item xs={11}>
          <Grid item xs={6}>
            <Typography variant="caption3Bold">
              {presetSelected?.name}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption3Light">Total Time: </Typography>
            <Typography variant="caption3Bold">
              {!!containsTATInstruction && '≈ '}
              {getTime(presetTime)}
            </Typography>
          </Grid>
        </Grid>
        <Grid className={styles.three_dots} item xs={1}>
          <IconButton onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>

          <Popover
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            id={id}
            onClose={handleClose}
            open={open}
          >
            <Typography className={styles.edit_text_button}  onClick={confirmDialog}>
              Edit
            </Typography>
          </Popover>
        </Grid>
      </Grid>
      <Typography
        className={styles.go_back_to_preset_text_button}
        onClick={() => setResetPopUp(true)}
        variant="caption3Light"
      >
        Go back to presets
      </Typography>
      {confirmPopUpOpen && (
        <PresetsConfirmDialog
          confirmPopUpOpen={confirmPopUpOpen}
          presetSelected={presetSelected}
          presetSettings={presetSettings}
          setConfirmPopUpOpen={setConfirmPopUpOpen}
          setIsPresetOpen={setIsPresetOpen}
          setPresetSelected={setPresetSelected}
          setSettings={setSettings}
          settings={settings}
        />
      )}
      {resetPopUp && (
        <PresetResetDialog
          resetPopUp={resetPopUp}
          setIsPresetOpen={setIsPresetOpen}
          setPresetSelected={setPresetSelected}
          setResetPopUp={setResetPopUp}
          setSettings={setSettings}
        />
      )}
    </Grid>
  );
};

export default PresetSelectedView;
